import { useEffect, useState, useCallback, Fragment } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtDT } from "../../lib/constants";
import { toast } from "sonner";
import { ArrowsClockwise, EnvelopeSimple, Warning } from "@phosphor-icons/react";
import AdminNav from "../../components/AdminNav";

const STATUS_FILTERS = ["", "FAILED", "SENT", "QUEUED"];

function fmt(iso) {
  return fmtDT(iso);
}

function StatusBadge({ status }) {
  const cls = status === "FAILED"
    ? "bg-red-500/15 text-red-400 border-red-500/30"
    : status === "SENT"
    ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
    : "bg-amber-500/15 text-amber-400 border-amber-500/30";
  return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${cls}`} data-testid={`email-status-${status}`}>{status}</span>;
}

export default function EmailHealth() {
  const { profile } = useAuth();
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ status: "FAILED", trigger: "", recipient: "", shipment_code: "" });

  const loadSummary = useCallback(() => {
    api.get("/admin/email-health/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const loadLogs = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    params.limit = 100;
    api.get("/admin/email-health/logs", { params })
      .then((r) => setLogs(r.data.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  if (profile && profile.role !== "SUPER_ADMIN") return <Navigate to="/" replace />;

  const resend = async (row) => {
    if (!row.resend_eligible) { toast.error("This email type cannot be resent here."); return; }
    if (!window.confirm(`Resend "${row.subject}" to ${row.recipient}?`)) return;
    setBusy(row.id);
    try {
      const { data } = await api.post(`/admin/email-health/logs/${row.id}/resend`);
      toast.success(`Resent (${data.status})`);
      loadSummary(); loadLogs();
    } catch (e) { toast.error(e.displayMessage || "Resend failed"); }
    finally { setBusy(null); }
  };

  const cards = summary ? [
    { label: "Total", value: summary.total, testid: "count-total" },
    { label: "Sent", value: summary.sent, testid: "count-sent" },
    { label: "Failed", value: summary.failed, testid: "count-failed" },
    { label: "Queued", value: summary.queued, testid: "count-queued" },
    { label: "Failed · 24h", value: summary.last_24h?.failed, testid: "count-failed-24h" },
    { label: "Failed · 7d", value: summary.last_7d?.failed, testid: "count-failed-7d" },
  ] : [];

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-6xl mx-auto p-6" data-testid="email-health-page">
      <div className="flex items-center gap-3 mb-6">
        <h1 className="heading text-3xl flex items-center gap-2"><EnvelopeSimple size={26} weight="bold" /> Email Health</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="grid-card p-4" data-testid={c.testid}>
            <div className="text-2xl font-bold">{c.value ?? "—"}</div>
            <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted))] mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid-card p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[11px] uppercase text-[hsl(var(--muted))] block mb-1">Status</label>
          <select className="input-field h-9 text-sm" value={filters.status} data-testid="filter-status"
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s || "All"}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase text-[hsl(var(--muted))] block mb-1">Trigger</label>
          <input className="input-field h-9 text-sm" value={filters.trigger} placeholder="e.g. DELIVERED" data-testid="filter-trigger"
            onChange={(e) => setFilters((f) => ({ ...f, trigger: e.target.value }))} />
        </div>
        <div>
          <label className="text-[11px] uppercase text-[hsl(var(--muted))] block mb-1">Recipient</label>
          <input className="input-field h-9 text-sm" value={filters.recipient} placeholder="email" data-testid="filter-recipient"
            onChange={(e) => setFilters((f) => ({ ...f, recipient: e.target.value }))} />
        </div>
        <div>
          <label className="text-[11px] uppercase text-[hsl(var(--muted))] block mb-1">Shipment code</label>
          <input className="input-field h-9 text-sm" value={filters.shipment_code} placeholder="A12-..." data-testid="filter-shipment"
            onChange={(e) => setFilters((f) => ({ ...f, shipment_code: e.target.value }))} />
        </div>
        <button className="btn-ghost h-9" onClick={() => loadLogs()} data-testid="apply-filters">Refresh</button>
      </div>

      <div className="grid-card grid-card-strong overflow-x-auto">
        {loading ? (
          <div className="p-6 text-sm text-[hsl(var(--muted))]">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="p-6 text-sm text-[hsl(var(--muted))]" data-testid="email-health-empty">
            {filters.status === "FAILED" && (summary?.total ?? 0) > 0 && (summary?.failed ?? 0) === 0
              ? "No failed emails found. Other email logs may be viewed by changing the Status filter to All."
              : "No emails match these filters."}
          </div>
        ) : (
          <div className="table-scroll">
          <table className="w-full text-xs" data-testid="email-health-table">
            <thead><tr className="text-left text-[hsl(var(--muted))]">
              <th className="p-2">Created</th><th className="p-2">Shipment</th><th className="p-2">Recipient</th>
              <th className="p-2">Trigger</th><th className="p-2">Subject</th><th className="p-2">Status</th>
              <th className="p-2">Backend</th><th className="p-2">Actions</th>
            </tr></thead>
            <tbody>
              {logs.map((r) => (
                <Fragment key={r.id}>
                  <tr className="border-t border-[hsl(var(--border))]" data-testid={`email-row-${r.id.slice(0, 6)}`}>
                    <td className="p-2 mono whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="p-2 mono">{r.shipment_code || "—"}</td>
                    <td className="p-2">{r.recipient}</td>
                    <td className="p-2 mono">{r.trigger}</td>
                    <td className="p-2 max-w-[220px] truncate" title={r.subject}>{r.subject}</td>
                    <td className="p-2"><StatusBadge status={r.status} /></td>
                    <td className="p-2 mono text-[hsl(var(--muted))]">{r.backend}</td>
                    <td className="p-2 whitespace-nowrap">
                      {r.error && (
                        <button className="btn-ghost text-[11px] mr-1" onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`email-error-toggle-${r.id.slice(0, 6)}`}>
                          <Warning size={12} /> Error
                        </button>
                      )}
                      {r.resend_eligible ? (
                        <button className="btn-ghost text-[11px]" disabled={busy === r.id} onClick={() => resend(r)} data-testid={`email-resend-${r.id.slice(0, 6)}`}>
                          <ArrowsClockwise size={12} /> {busy === r.id ? "…" : "Resend"}
                        </button>
                      ) : (
                        <span className="text-[10px] text-[hsl(var(--muted))]" data-testid={`email-resend-blocked-${r.id.slice(0, 6)}`}>OTP · no resend</span>
                      )}
                    </td>
                  </tr>
                  {expanded === r.id && r.error && (
                    <tr><td colSpan={8} className="p-2 bg-[hsl(var(--muted)/0.06)]">
                      <pre className="text-[11px] text-red-400 whitespace-pre-wrap" data-testid={`email-error-detail-${r.id.slice(0, 6)}`}>{r.error}</pre>
                    </td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
